import { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";

export function ApkUploadDialog({ open, onOpenChange, onDone, nextCode }: { open: boolean; onOpenChange: (o: boolean) => void; onDone: () => void; nextCode: number }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("version_name", name.trim());
    fd.append("version_code", code || String(nextCode));
    fd.append("notes", notes);
    fd.append("mandatory", String(mandatory));
    setBusy(true); setProgress(0);
    try {
      await api.post("/admin/apk-releases", fd, { onUploadProgress: (e) => setProgress(Math.round(((e.loaded || 0) / (e.total || 1)) * 100)) });
      toast.success("APK téléversé — publiez-le pour l'envoyer aux télés");
      setFile(null); setName(""); setCode(""); setNotes(""); setMandatory(false);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="apk-upload-dialog" className="border-slate-800 bg-slate-900">
        <DialogHeader><DialogTitle>Nouvelle version de l'application TV</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Fichier APK</Label><Input type="file" accept=".apk,application/vnd.android.package-archive" data-testid="apk-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="border-slate-700 bg-slate-950/60" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Nom de version</Label><Input data-testid="apk-version-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="1.4.0" className="font-mono border-slate-700 bg-slate-950/60" /></div>
            <div className="space-y-2"><Label>Code de version</Label><Input type="number" min={1} data-testid="apk-version-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={String(nextCode)} className="font-mono border-slate-700 bg-slate-950/60" /></div>
          </div>
          <p className="text-xs text-slate-500">Le code doit être identique au <span className="font-mono">versionCode</span> de l'APK (build.gradle) et plus grand que celui installé sur les télés.</p>
          <div className="space-y-2"><Label>Notes de version</Label><Textarea data-testid="apk-notes-input" rows={3} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex. : support des transitions vidéo" className="border-slate-700 bg-slate-950/60" /></div>
          <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
            <div><p className="text-sm text-slate-200">Mise à jour obligatoire</p><p className="text-xs text-slate-500">La télé installe dès réception, sans attendre la nuit.</p></div>
            <Switch data-testid="apk-mandatory-switch" checked={mandatory} onCheckedChange={setMandatory} />
          </div>
          <Button data-testid="apk-upload-submit" disabled={!file || !name.trim() || busy} onClick={submit} className="w-full bg-blue-600 text-white hover:bg-blue-500"><Upload className="mr-1 h-4 w-4" />{busy ? `Envoi… ${progress} %` : "Téléverser"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
